import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { API_BASE_URL } from "../config/api";
import { PRICING } from "../config/pricing";

const AddPetModal = ({ showModal, closeModal, refreshPets }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [petData, setPetData] = useState({
    name: "",
    species: "",
    breed: "",
    age: "",
    gender: "",
    size: "",
    spayedNeutered: false,
    photoFile: null,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      setPetData((prev) => ({
        ...prev,
        photoFile: files[0] || null,
      }));
    } else {
      setPetData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!petData.name.trim()) validationErrors.name = "Pet name is required.";
    if (!petData.species) validationErrors.species = "Species is required.";
    if (!petData.breed.trim()) validationErrors.breed = "Breed is required.";
    if (!petData.age || Number(petData.age) < 0)
      validationErrors.age = "Valid age is required.";
    if (!petData.gender) validationErrors.gender = "Gender is required.";
    if (!petData.size) validationErrors.size = "Size is required.";

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("authToken");
    if (!token) {
      setToast({ show: true, message: "You must be logged in to add a pet.", type: "error" });
      closeModal();
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      const parseJwtPayload = (jwtToken) => {
        const payload = jwtToken?.split?.(".")?.[1];
        if (!payload) return null;
        try {
          const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
          const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
          return JSON.parse(atob(padded));
        } catch {
          return null;
        }
      };

      const decodedToken = parseJwtPayload(token);
      const userId = user?._id || decodedToken?.userId;
      if (!userId) {
        setToast({
          show: true,
          message: "We couldn't identify your account. Please log out and log back in.",
          type: "error",
        });
        setIsLoading(false);
        return;
      }

      const sizeKey = petData.size.toLowerCase();
      const monthlyPrice = PRICING.MEMBERSHIP_MONTHLY_RANDS;

      let photoUrl = null;
      if (petData.photoFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("photo", petData.photoFile);

        const uploadRes = await fetch(`${API_BASE_URL}/api/pets/upload-photo`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: uploadFormData,
        });

        if (!uploadRes.ok) {
          const errorData = await uploadRes.json().catch(() => ({}));
          console.error("Error uploading photo:", errorData);
          setToast({ show: true, message: "Could not upload pet photo. Please try again.", type: "error" });
          setIsLoading(false);
          return;
        }

        const uploaded = await uploadRes.json();
        photoUrl = uploaded?.photoUrl || null;
      }

      setToast({ show: true, message: "Redirecting to secure checkout…", type: "success" });
      closeModal();

      const petDraft = {
        userId,
        name: petData.name.trim(),
        species: petData.species,
        breed: petData.breed.trim(),
        age: Number(petData.age),
        gender: petData.gender,
        size: sizeKey,
        spayedNeutered: !!petData.spayedNeutered,
        photoUrl,
      };

      navigate("/checkout", {
        state: {
          membership: true,
          total: monthlyPrice,
          package: { type: "Subscription" },
          membershipObjectId: null,
          selectedPets: [],
          petDraft,
          order: {
            tagCount: 0,
            tagUnitPrice: PRICING.TAG_ONCE_OFF_RANDS,
            membershipCount: 1,
            membershipUnitPrice: monthlyPrice,
          },
        },
      });
    } catch (error) {
      console.error("Error adding pet:", error);
      setToast({ show: true, message: "An error occurred. Please try again.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Toast auto-hide effect
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((t) => ({ ...t, show: false })), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Reusable input component
  const renderInput = (label, name, type = "text") => (
    <div className="col-md-6 mb-3">
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        className={`form-control ${errors[name] ? "is-invalid" : ""}`}
        value={petData[name]}
        onChange={handleInputChange}
        disabled={isLoading}
      />
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  // Reusable select component
  const renderSelect = (label, name, options) => (
    <div className="col-md-6 mb-3">
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <select
        id={name}
        name={name}
        className={`form-select ${errors[name] ? "is-invalid" : ""}`}
        value={petData[name]}
        onChange={handleInputChange}
        disabled={isLoading}
      >
        <option value="">{`Select ${label.toLowerCase()}`}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  if (!showModal) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040, backgroundColor: "rgba(0,0,0,0.5)" }}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        aria-labelledby="addPetModalLabel"
        aria-modal="true"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow rounded">
            <div className="modal-header border-0">
              <h5 className="modal-title" id="addPetModalLabel">
                Add New Pet
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeModal}
                disabled={isLoading}
              />
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit} noValidate>
                <div className="row">
                  {renderInput("Pet Name", "name")}
                  {renderSelect("Species", "species", ["Dog", "Cat"])}
                  {renderInput("Breed", "breed")}
                  {renderInput("Age", "age", "number")}
                  {renderSelect("Gender", "gender", ["Male", "Female"])}
                  {renderSelect("Size", "size", ["Small", "Medium", "Large"])}

                  <div className="col-md-6 mb-3">
                    <label htmlFor="photo" className="form-label">
                      Pet Image
                    </label>
                    <input
                      id="photo"
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-md-6 mb-3 d-flex align-items-center">
                    <div className="form-check">
                      <input
                        id="spayedNeutered"
                        type="checkbox"
                        name="spayedNeutered"
                        className="form-check-input"
                        checked={petData.spayedNeutered}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                      <label
                        htmlFor="spayedNeutered"
                        className="form-check-label ms-2"
                      >
                        Spayed / Neutered
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-primary fw-semibold d-flex justify-content-center align-items-center"
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                    Add Pet & Checkout
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`toast align-items-center text-white ${
            toast.type === "success" ? "bg-success" : "bg-danger"
          } border-0 show`}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          style={{
            position: "fixed",
            bottom: "1rem",
            right: "1rem",
            zIndex: 1060,
            minWidth: "250px",
          }}
        >
          <div className="d-flex">
            <div className="toast-body">{toast.message}</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              aria-label="Close"
              onClick={() => setToast((t) => ({ ...t, show: false }))}
            ></button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddPetModal;
